import Typography from "@mui/material/Typography";
import {Box, IconButton, List, ListItem, Stack} from "@mui/material";
import * as React from "react";
import {useTranslation} from "react-i18next";
import Button from "@mui/material/Button";
import {useNavigate} from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from '@mui/icons-material/Search';
import {formatFullDateTime} from "crqlar-react-common/dist/utils/date.util";
import NewRateDialog from "crqlar-react-common/dist/components/NewRateDialog";
import PickRateDialog from "crqlar-react-common/dist/components/PickRateDialog";
import {useRatesQ} from "crqlar-react-common/dist/utils/hooks/rates/useRates";
import {Service} from "crqlar-react-common/dist/models/service/service.model";
import {getAmountFormatted} from "crqlar-react-common/dist/utils/currency.util";
import {RateService} from "crqlar-react-common/dist/services/rate/rate.service";

interface Props {
    setService: (service: Service) => void
    service: Service
}

const RatePreview = (props: Props) => {

    const navigate = useNavigate()
    const {t} = useTranslation()
    const [newRateOpen, setNewRateOpen] = React.useState(false)
    const [existingRateOpen, setExistingRateOpen] = React.useState(false)
    const ratesQ = useRatesQ(props.service?.rateIds || [])

    const renderRatesQ = () => {
        if (ratesQ.isLoading) {
            return (
                <Typography
                    variant={'caption'}
                    textAlign={'start'}
                >
                    {t('Loading...')}
                </Typography>
            )
        }

        if (ratesQ.isError) {
            return (
                <Typography
                    variant={'caption'}
                    textAlign={'start'}
                >
                    {t('We could not load rates')}
                </Typography>
            )
        }

        if (!ratesQ.data?.content?.length) {
            // return (
            //     <Typography
            //         variant={'caption'}
            //         textAlign={'start'}
            //     >
            //         {t('No rates found')}
            //     </Typography>
            // )
            return null
        }

        let itemsToDisplay = ratesQ.data.content || []

        return (
            <Stack>
                <Typography
                    variant={'caption'}
                    textAlign={'start'}
                >
                    {t('Rates/Prices in the next month')}
                </Typography>
                <List
                    dense
                >
                    {
                        itemsToDisplay.map((rate, index) => {
                            return (
                                <ListItem
                                    key={rate.id}
                                    id={`rate-${index}`}
                                >
                                    <Box
                                        sx={{
                                            width: '8px',
                                            height: '8px',
                                            borderRadius: '50%',
                                            backgroundColor: 'lightgreen',
                                            mr: 1
                                        }}
                                    />

                                    <Stack
                                        direction={'row'}
                                        alignItems={'center'}
                                        gap={2}
                                        width={'100%'}
                                    >
                                        <Typography sx={{width: 100}}>
                                            {getAmountFormatted(rate.price)}
                                        </Typography>

                                        <Typography sx={{flex: 1}}>
                                            {rate.name}
                                        </Typography>

                                        <Typography sx={{width: 200}}>
                                            {[formatFullDateTime(rate.startTime) || t('no start'), formatFullDateTime(rate.endTime) || t('no end')].join(' - ')}
                                        </Typography>

                                        <Button
                                            endIcon={<OpenInNewIcon/>}
                                            sx={{minWidth: 'fit-content'}}
                                            onClick={() => {
                                                window.open(`/system/rates/${rate.id}`, '_blank')
                                            }}
                                        >
                                            {t('View rate')}
                                        </Button>

                                        <IconButton
                                            onClick={() => {
                                                const newRates = props.service.rateIds?.filter((id) => id !== rate.id)
                                                props.setService({
                                                    ...props.service,
                                                    rateIds: newRates
                                                })
                                            }}
                                        >
                                            <DeleteIcon/>
                                        </IconButton>

                                    </Stack>

                                </ListItem>
                            )
                        })
                    }
                </List>

            </Stack>
        )
    }

    return (
        <Stack>

            <Stack
                direction={'row'}
                justifyContent={'space-between'}
                alignItems={'center'}
            >
                <Typography variant={'h6'}>
                    {t('Rates / prices')} ({ratesQ.data?.totalElements || 0})
                </Typography>

                <Stack direction={'row'} gap={2}>
                    <Button
                        id={'btn-plan-rate'}
                        variant={'contained'}
                        startIcon={<SearchIcon/>}
                        onClick={() => {
                            setExistingRateOpen(true)
                        }}
                    >
                        {t('Attach existing rate')}
                    </Button>

                    <Button
                        id={'btn-plan-rate'}
                        variant={'contained'}
                        startIcon={<AddIcon/>}
                        onClick={() => {
                            setNewRateOpen(true)
                        }}
                    >
                        {t('Create new rate')}
                    </Button>
                </Stack>

            </Stack>


            {renderRatesQ()}

            <NewRateDialog
                open={newRateOpen}
                onClose={() => setNewRateOpen(false)}
                onConfirm={async rate => {
                    let rateId = rate.id

                    if (!rateId) {
                        const newRate = await RateService.createRate(rate)
                        rateId = newRate.id
                    }

                    const newRates = [...(props.service.rateIds || [])]
                    newRates.push(rateId!)
                    props.setService({
                        ...props.service,
                        rateIds: newRates
                    })
                    setNewRateOpen(false)
                }}
                defaultName={props.service?.name}
            />

            <PickRateDialog
                open={existingRateOpen}
                onClose={() => setExistingRateOpen(false)}
                onConfirm={rate => {
                    const newRates = [...(props.service.rateIds || [])]
                    newRates.push(rate.id!)
                    props.setService({
                        ...props.service,
                        rateIds: newRates
                    })
                    setExistingRateOpen(false)
                }}
            />

        </Stack>
    )
}

export default RatePreview;
